import { useState, useRef, useCallback, useEffect } from 'react';
import { playerRepository } from '../../data/playerRepository';
import { selecionarVideoInvasao } from '@/features/jukebox';
import type { FilaJukebox, PlaylistItem, FonteReproducao } from '@/shared/types';

export interface UseJukeboxQueueParams {
  telaId:      string;
  empresaId:   string | undefined;
  hasPlaylist: boolean;
  sendToTela:  (event: string, payload: Record<string, unknown>) => void;
  broadcastMusicaAtual:  (item: PlaylistItem | FilaJukebox) => void;
  mostrarPedidoPopup:    (item: FilaJukebox) => void;
  avancarDeck:           () => number | undefined;
  configInvasaoJunina:   boolean;
  configIntervaloJunina: number;
  /** Origem de mídia atual do Player — usada para não avançar a fila quando o
   *  Player está em modo transmissão com áudio vindo da captura (o onEnded do
   *  YouTube nesse caso não pertence ao Jukebox). Preservado do handleYtEnded
   *  original (pré-extração); não fazia parte da assinatura original do plano. */
  origemMidia:  FonteReproducao;
  audioCaptura: 'playlist' | 'captura';
}

export interface UseJukeboxQueueReturn {
  pedidoAtual:    FilaJukebox | null;
  pedidoAtualRef: React.RefObject<FilaJukebox | null>;
  isInvasaoAtiva: boolean;
  filaEspera:     FilaJukebox[];
  filaEsperaRef:  React.RefObject<FilaJukebox[]>;
  handleYtEnded(): Promise<void>;
  adicionarPedido(item: FilaJukebox): void;
  removerPedido(id: string): void;
  resetarFila(): void;
  /** Reconcilia fila em memória com o banco (pendente/tocando). Usado no active sync e após queda de rede. */
  reconciliarFila(): Promise<void>;
}

/** Reconcilia fila em memória com o snapshot ativo do banco (pendente/tocando). Exportada para teste. */
export function reconciliarFilaComBanco(
  ativos: FilaJukebox[],
  filaEsperaRef: React.RefObject<FilaJukebox[]>,
  pedidoAtualRef: React.RefObject<FilaJukebox | null>,
  setPedidoAtual: React.Dispatch<React.SetStateAction<FilaJukebox | null>>,
  setIsInvasaoAtiva: React.Dispatch<React.SetStateAction<boolean>>,
  publicarFila: (items: FilaJukebox[]) => void,
): void {
  const idsAtivos = new Set(ativos.map(i => i.id));

  filaEsperaRef.current = filaEsperaRef.current.filter(i => idsAtivos.has(i.id));

  const pedido = pedidoAtualRef.current;
  if (pedido && !pedido.is_invasao_tematica && !idsAtivos.has(pedido.id)) {
    pedidoAtualRef.current = null;
    setPedidoAtual(null);
    setIsInvasaoAtiva(false);
  }

  const pendentes = ativos.filter(i => i.status === 'pendente');
  const idsNaMemoria = new Set(filaEsperaRef.current.map(i => i.id));
  // O pedido que está tocando pode ainda constar `pendente` no banco (o UPDATE
  // para 'tocando' falhou ou não terminou) — não devolvê-lo à fila, senão a
  // música do cliente toca duas vezes.
  if (pedidoAtualRef.current) idsNaMemoria.add(pedidoAtualRef.current.id);
  const faltantes = pendentes.filter(i => !idsNaMemoria.has(i.id));
  if (faltantes.length > 0) {
    filaEsperaRef.current.push(...faltantes);
  }
  publicarFila([...filaEsperaRef.current]);
}

/** Fila do Jukebox: pedidos pendentes, item tocando agora, injeção da
 *  Invasão da Festa Junina, active sync backup (reconciliação a cada 3 min
 *  contra event drop do Realtime), e o handler central `onEnded` do
 *  YoutubeSlide que avança para o próximo pedido ou item do deck da playlist. */
export function useJukeboxQueue({
  telaId, empresaId, hasPlaylist, sendToTela, broadcastMusicaAtual,
  mostrarPedidoPopup, avancarDeck, configInvasaoJunina, configIntervaloJunina,
  origemMidia, audioCaptura,
}: UseJukeboxQueueParams): UseJukeboxQueueReturn {
  const filaEsperaRef = useRef<FilaJukebox[]>([]);
  const [filaEspera, setFilaEspera] = useState<FilaJukebox[]>([]);
  const [pedidoAtual, setPedidoAtual] = useState<FilaJukebox | null>(null);
  const pedidoAtualRef = useRef<FilaJukebox | null>(null);
  const [isInvasaoAtiva, setIsInvasaoAtiva] = useState(false);
  const contadorMusicasRef = useRef(0);
  const isAdvancingRef = useRef(false);

  const publicarFila = useCallback((items: FilaJukebox[]) => {
    filaEsperaRef.current = items;
    setFilaEspera(items);
  }, []);

  useEffect(() => {
    if (!telaId || !hasPlaylist) return;
    playerRepository.resolverTocandoDuplicados(telaId)
      .catch(err => console.warn('[useJukeboxQueue] resolverTocandoDuplicados:', err))
      .then(() => playerRepository.listarAtivosJukebox(telaId))
      .then(items => {
        const tocando = items.find(i => i.status === 'tocando') ?? null;
        if (tocando) { pedidoAtualRef.current = tocando; setPedidoAtual(tocando); }
        publicarFila(items.filter(i => i.status === 'pendente'));
      })
      .catch(err => console.warn('[useJukeboxQueue] init fila:', err));
  }, [telaId, hasPlaylist, publicarFila]);

  const resetarFila = useCallback(() => {
    publicarFila([]);
    pedidoAtualRef.current = null;
    setPedidoAtual(null);
    setIsInvasaoAtiva(false);
  }, [publicarFila]);

  const reconciliarFila = useCallback(async () => {
    if (!telaId || !hasPlaylist) return;
    try {
      await playerRepository.resolverTocandoDuplicados(telaId)
        .catch(err => console.warn('[useJukeboxQueue] reconciliar resolver:', err));
      const ativos = await playerRepository.listarAtivosJukebox(telaId);
      reconciliarFilaComBanco(
        ativos, filaEsperaRef, pedidoAtualRef, setPedidoAtual, setIsInvasaoAtiva, publicarFila,
      );
    } catch (err) {
      console.warn('[useJukeboxQueue] reconciliar fila falhou:', err);
    }
  }, [telaId, hasPlaylist, publicarFila]);

  // Active Sync Backup: reconcilia contra o banco (add faltantes + prune obsoletos).
  useEffect(() => {
    if (!telaId || !hasPlaylist) return;
    const ACTIVE_SYNC_MS = 3 * 60 * 1_000;

    const interval = setInterval(() => { void reconciliarFila(); }, ACTIVE_SYNC_MS);

    return () => clearInterval(interval);
  }, [telaId, hasPlaylist, reconciliarFila]);

  const adicionarPedido = useCallback((item: FilaJukebox) => {
    if (filaEsperaRef.current.some(i => i.id === item.id)) return;
    if (pedidoAtualRef.current?.id === item.id) return;
    publicarFila([...filaEsperaRef.current, item]);
  }, [publicarFila]);

  const removerPedido = useCallback((id: string) => {
    publicarFila(filaEsperaRef.current.filter(i => i.id !== id));
    if (pedidoAtualRef.current?.id === id) {
      pedidoAtualRef.current = null;
      setPedidoAtual(null);
      setIsInvasaoAtiva(false);
    }
  }, [publicarFila]);

  const handleYtEnded = useCallback(async () => {
    if (origemMidia === 'transmissao' && audioCaptura === 'captura') return;
    if (isAdvancingRef.current) return;
    isAdvancingRef.current = true;

    try {
      const eraInvasao = pedidoAtualRef.current?.is_invasao_tematica === true;

      if (pedidoAtualRef.current) {
        if (!eraInvasao) {
          await playerRepository.atualizarStatusJukebox(pedidoAtualRef.current.id, 'concluido', telaId)
            .catch(err => console.warn('[useJukeboxQueue] concluir pedido:', err));
        }
        pedidoAtualRef.current = null;
        setPedidoAtual(null);
        setIsInvasaoAtiva(false);
      }

      if (!eraInvasao) {
        contadorMusicasRef.current += 1;
        if (
          configInvasaoJunina &&
          configIntervaloJunina > 0 &&
          contadorMusicasRef.current % configIntervaloJunina === 0
        ) {
          const video = selecionarVideoInvasao();
          publicarFila([{
            id:                  crypto.randomUUID(),
            tela_id:             telaId,
            empresa_id:          empresaId ?? '',
            youtube_video_id:    video.videoId,
            titulo:              video.titulo,
            thumbnail:           video.thumbnail,
            status:              'pendente',
            criado_em:           new Date().toISOString(),
            is_invasao_tematica: true,
            vibe:                'neutro',
          }, ...filaEsperaRef.current]);
        }
      }

      if (filaEsperaRef.current.length > 0) {
        const [proximo, ...resto] = filaEsperaRef.current;
        publicarFila(resto);
        pedidoAtualRef.current = proximo;
        setPedidoAtual(proximo);
        setIsInvasaoAtiva(proximo.is_invasao_tematica === true);
        broadcastMusicaAtual(proximo);
        if (!proximo.is_invasao_tematica) {
          sendToTela('pedido_iniciado', {
            youtube_video_id: proximo.youtube_video_id,
            titulo:           proximo.titulo,
            thumbnail:        proximo.thumbnail,
            session_id:       proximo.session_id,
            cliente_nome:       proximo.cliente_nome,
            cliente_avatar_url: proximo.cliente_avatar_url,
          });
          mostrarPedidoPopup(proximo);
          await playerRepository.atualizarStatusJukebox(proximo.id, 'tocando', telaId)
            .catch(err => console.warn('[useJukeboxQueue] marcar tocando:', err));
        }
      } else {
        setIsInvasaoAtiva(false);
        avancarDeck();
      }
    } finally {
      isAdvancingRef.current = false;
    }
  }, [
    telaId, empresaId, configInvasaoJunina, configIntervaloJunina, broadcastMusicaAtual,
    sendToTela, mostrarPedidoPopup, avancarDeck, origemMidia, audioCaptura, publicarFila,
  ]);

  return {
    pedidoAtual, pedidoAtualRef, isInvasaoAtiva, filaEspera, filaEsperaRef,
    handleYtEnded, adicionarPedido, removerPedido, resetarFila, reconciliarFila,
  };
}
